import axios from 'axios'
import { parseCookies, destroyCookie } from 'nookies'

import * as LocalStorage from './localStorage'
import { USE_PARSE_PROXY } from '~/utilities/constants'

// Parse error codes for a session token the backend rejected:
//   209 = INVALID_SESSION_TOKEN, 208 = INVALID_LINKED_SESSION
// The backend also maps these to HTTP 401. Such a token is dead (expired /
// revoked / user deleted); replaying it just produces a 209 on every call.
// secure() reads the token from BOTH localStorage and the cookie (cookie wins),
// so both must be purged or the stale token keeps coming back.
const PARSE_INVALID_SESSION_CODES = [208, 209]

const isStaleSessionError = error => {
  const status = error?.response?.status
  const code = error?.response?.data?.code
  return (
    status === 401 ||
    (typeof code === 'number' && PARSE_INVALID_SESSION_CODES.includes(code))
  )
}

const clearStoredSession = () => {
  LocalStorage.remove('sessionToken')
  // Mirror authStore.unsetUser(): the cookie is written with a domain in
  // non-dev, so it must be removed with the same path/domain to actually clear.
  const options = { path: '/' }
  if (process.env.WINIT_ENV !== 'development' && typeof window !== 'undefined') {
    options.domain = window.location.hostname.replace('www', '')
  }
  destroyCookie({}, 'sessionToken', options)
}

// CONFIGURATIONS
// For calls to parse backend
// "application-name" headers allow the back end to identify the source of calls (we don't use "origin", we want a header fixed that will be also used by the jobs)
const generalConfigHeaders = {"application-name": "winit-webapp"} // todo : add -prod / -staging depending on the environment
if(!USE_PARSE_PROXY) generalConfigHeaders['X-Parse-Application-Id'] = process.env.PARSE_APP_ID

const generalConfig = {
  baseURL: process.env.PRIMARY_API_URL,
  headers: generalConfigHeaders
}
// For calls to insurance backend
const insuranceBackendConfigV1 = {
  baseURL: `${process.env.INSURANCE_API_URL}/api/v1`,
  headers: {
    'X-Parse-Application-Id': process.env.PARSE_APP_ID
  }
}

// INSTANCES
export const secure = (
  sessionTokenOverride,
  config = generalConfig,
  additionalHeaders = {}
) => {
  let sessionToken
  if (sessionTokenOverride) {
    sessionToken = sessionTokenOverride
  } else {
    const localSessionToken = LocalStorage.get('sessionToken')
    if (localSessionToken) {
      sessionToken = localSessionToken
    }
    const cookies = parseCookies({})
    const cookieSessionToken = cookies.sessionToken
    if (cookieSessionToken) {
      // Cookies have priority because SSR w/ hydrated user store
      sessionToken = cookieSessionToken
    }
  }

  const headers = { ...config.headers, ...additionalHeaders }
  if (sessionToken) {
    // When using parse proxy && making requests to PARSE proxy use custom header
    if (USE_PARSE_PROXY && config.baseURL === process.env.PRIMARY_API_URL) {
      headers.sessionToken = sessionToken
    } else {
      headers['X-Parse-Session-Token'] = sessionToken
    }
  }

  const instance = axios.create({
    ...config,
    headers
  })

  // Drop the stored session as soon as the backend reports it is invalid, so a
  // single stale token can't generate an unbounded stream of 209s across every
  // authenticated call. Only clears when a token was actually sent.
  if (sessionToken) {
    instance.interceptors.response.use(
      response => response,
      error => {
        if (isStaleSessionError(error)) {
          clearStoredSession()
        }
        return Promise.reject(error)
      }
    )
  }

  return instance
}
export const insuranceSecure = sessionToken =>
  secure(sessionToken, insuranceBackendConfigV1)
export const insurance = axios.create({ ...insuranceBackendConfigV1 })
export const basic = axios.create({ ...generalConfig })

// HELPERS
/**
 * Gets a string of the error message from an object.
 * @param {*} error This can take either a javascript Error object or a axios JSON formatted error response
 */
export const parseError = error => {
  // Generic Catch All
  let result = 'Unexpected error.'

  // Axios threw an error
  if (error && error.message && typeof error.message === 'string') {
    result = error.message
  }

  // New Routes throw an api error
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.message &&
    typeof error.response.data.message === 'string'
  ) {
    result = error.response.data.message
  }

  // Parse/Parse related stuff threw an error
  if (
    error &&
    error.response &&
    error.response.data &&
    error.response.data.error &&
    typeof error.response.data.error === 'string'
  ) {
    result = error.response.data.error
  }

  return Promise.reject(result)
}
